import { useState } from "react";
import styled, { keyframes } from "styled-components";
import { FiLoader, FiLock } from "react-icons/fi";
import { WHITE, TEXT_LEAD, TINT_GREEN, WASH, GREEN_ACCENT, BORDER_GREEN, INK, BORDER_SUBTLE, SEMAPHORE, SURFACE_CARD, TEXT_VALUE, type SemaphoreTone } from "../tokens";

// A card in the band under the verdict. By default it is an upsell: dashed,
// padlocked, with the tier it unlocks. Give it a `value` instead and it shows
// a figure the report already has — solid border, no padlock, nothing to click.
export interface ExecutiveUpgradeCard {
  title: string;
  availability?: string;
  actionLabel?: string;
  value?: string;
  hint?: string;
  // Colours the figure when it is itself the finding — a mortgage charge reads
  // red. Defaults to the same ink as the rest of the report.
  severity?: SemaphoreTone;
}

export interface ExecutiveUpgradePanel {
  title: string;
  description: string;
  items?: string[];
  actions: Array<{ label: string; href: string }>;
}

export interface ExecutiveUpgrades {
  cards: ExecutiveUpgradeCard[];
  panel: ExecutiveUpgradePanel;
}

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 26px 0 0;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;
const Card = styled.button`
  background: ${SURFACE_CARD};
  border: 1px dashed #d8dedb;
  border-radius: 14px;
  padding: 20px;
  text-align: left;
  cursor: pointer;
  font: inherit;
  &:hover { border-color: ${INK.ghost}; background: ${WASH}; }
  &:focus-visible { outline: 2px solid ${SEMAPHORE.green.text}; outline-offset: 3px; }
  &:disabled { cursor: default; }
`;
const CardTitle = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-size: 10px; font-weight: 600; text-transform: uppercase;
  letter-spacing: .06em; color: ${INK.label};
`;
const DataCard = styled.div`
  background: ${SURFACE_CARD};
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 14px;
  padding: 20px;
`;
const SEVERITY_COLOR: Record<string, string> = {
  green: SEMAPHORE.green.text,
  amber: SEMAPHORE.amber.text,
  red: SEMAPHORE.red.text,
};
const DataValue = styled.div<{ $severity?: string }>`
  margin-top: 10px; font-size: 19px; font-weight: 700; letter-spacing: -0.02em;
  color: ${(p) => (p.$severity && SEVERITY_COLOR[p.$severity]) || TEXT_VALUE};
`;
const DataHint = styled.div`
  margin-top: 6px; font-size: 11.5px; line-height: 1.45; color: ${INK.soft};
`;
const Availability = styled.div`
  margin-top: 18px; font-size: 17px; font-weight: 700; color: ${INK.quiet};
`;
const ActionLabel = styled.div`
  margin-top: 8px; font-size: 12px; font-weight: 600; color: ${GREEN_ACCENT};
`;
const Panel = styled.section`
  margin-top: 40px; padding: 30px 32px; border-radius: 18px;
  border: 1px solid ${BORDER_GREEN}; background: linear-gradient(160deg,${TINT_GREEN[50]},${SURFACE_CARD});
  h2 { display: flex; align-items: center; gap: 11px; margin: 0 0 14px; font-size: 17px; color: ${TEXT_VALUE}; }
  p { margin: 0 0 18px; max-width: 640px; font-size: 13.5px; line-height: 1.6; color: ${INK.soft}; }
`;
// Each item is a pill led by a dot, the way the design lists what the next
// tier unlocks.
const Items = styled.div`
  display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 22px;
  span {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 7px 13px; border: 1px solid ${BORDER_GREEN}; border-radius: 100px;
    background: white; font-size: 12px; color: ${INK.strong};
  }
  span::before {
    content: ""; flex-shrink: 0;
    width: 5px; height: 5px; border-radius: 50%; background: ${SEMAPHORE.green.fill};
  }
`;
const spin = keyframes`
  to { transform: rotate(360deg); }
`;
const Spinner = styled(FiLoader)`
  vertical-align: -2px;
  margin-left: 6px;
  animation: ${spin} 1.2s linear infinite;
`;
const Actions = styled.div`
  display: flex; flex-wrap: wrap; gap: 10px;
  a, button { position: relative; padding: 12px 20px; border: 1px solid ${BORDER_GREEN}; border-radius: 10px; background: ${WHITE}; color: ${TEXT_LEAD}; font: inherit; font-size: 13px; font-weight: 600; text-decoration: none; cursor: pointer; }
  a:first-child, button:first-child { background: ${TEXT_VALUE}; color: ${WHITE}; }
  a:focus-visible, button:focus-visible { outline: 2px solid ${SEMAPHORE.green.text}; outline-offset: 3px; }
  button:disabled { cursor: default; }
  /* While redirecting, the spinner takes the label's place without resizing the button. */
  button[aria-busy="true"] > span { visibility: hidden; }
  button[aria-busy="true"] > svg { position: absolute; inset: 0; margin: auto; }
`;

/**
 * The level an upsell CTA unlocks, read from its copy ("Disponible en Nivel 3",
 * "Ampliar a Nivel 2 · €499"): the fixtures carry no structured tier.
 * @returns 1-3, or null when the copy names no level.
 */
export const upsellLevel = (text?: string): number | null => {
  const match = text?.match(/\b(?:nivel|nivell|level)\s*0?([1-3])\b/i);
  return match ? Number(match[1]) : null;
};

// Only navigation links; rendering an upgrade never starts checkout or sends email.
const safeHref = (href: string) => /^(https?:\/\/|mailto:|\/(?!\/)|#)/i.test(href) ? href : undefined;

interface UpgradesProps {
  data: ExecutiveUpgrades;
  /** Starts the checkout for the level a CTA names (null when it names none). Absent when the case can't be upgraded. */
  onUpsell?: (level: number | null) => void;
  /** True while the checkout link is being fetched: every CTA locks and the clicked one spins. */
  upsellPending?: boolean;
}

export const ExecutiveSummaryUpgrades = ({ data, onUpsell, upsellPending }: UpgradesProps) => {
  const [clicked, setClicked] = useState<string | null>(null);
  const upsell = (key: string, text?: string) => {
    setClicked(key);
    onUpsell?.(upsellLevel(text));
  };
  const busy = (key: string) => !!upsellPending && clicked === key;

  return (
    <>
      <Cards>
        {data.cards.map((card, index) => card.value
          ? <DataCard key={index}>
              <CardTitle>{card.title}</CardTitle>
              <DataValue $severity={card.severity}>{card.value}</DataValue>
              {card.hint && <DataHint>{card.hint}</DataHint>}
            </DataCard>
          : <Card
              key={index}
              type="button"
              disabled={upsellPending}
              aria-busy={busy(`card-${index}`)}
              onClick={() => onUpsell
                ? upsell(`card-${index}`, card.availability ?? card.actionLabel)
                : document.getElementById("executive-upgrades")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            >
              <CardTitle><FiLock size={14} />{card.title}</CardTitle>
              <Availability>{card.availability}</Availability>
              <ActionLabel>{card.actionLabel}{busy(`card-${index}`) && <Spinner size={12} />}</ActionLabel>
            </Card>)}
      </Cards>
      <Panel id="executive-upgrades" aria-label={data.panel.title}>
        <h2><FiLock size={17} />{data.panel.title}</h2>
        <p>{data.panel.description}</p>
        {data.panel.items?.length ? <Items>{data.panel.items.map((item, i) => <span key={i}>{item}</span>)}</Items> : null}
        <Actions>{data.panel.actions.map((action, i) => onUpsell
          ? <button key={i} type="button" disabled={upsellPending} aria-busy={busy(`action-${i}`)} onClick={() => upsell(`action-${i}`, action.label)}>
              <span>{action.label}</span>
              {busy(`action-${i}`) && <Spinner size={14} />}
            </button>
          : <a key={i} href={safeHref(action.href)}>{action.label}</a>)}</Actions>
      </Panel>
    </>
  );
};
